// React imports
import React, { useState, useMemo, useEffect } from 'react';

// General imports
import _ from 'lodash';

// MUI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports (fallback if lucide-react not available)
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/pro-light-svg-icons';

// Try to import lucide-react, fallback to FontAwesome
let FilterIcon = null;
try {
    const lucideReact = require('lucide-react');
    FilterIcon = lucideReact.Filter;
} catch (e) {
    // Fallback to FontAwesome
    FilterIcon = ({ size, style }) => <FontAwesomeIcon icon={faFilter} style={style} />;
}

function FiltersPanel(props) {
    const { results, filters, setFilters } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();

    const isOpen = Boolean(anchorEl);

    const nonErrorResults = useMemo(() => {
        return results ? results.filter(r => !r.error) : [];
    }, [results]);

    // Extract unique values for filters
    const productTypes = useMemo(() => {
        return _.uniq(nonErrorResults.map(r => r.product?.type).filter(Boolean));
    }, [nonErrorResults]);

    const carriers = useMemo(() => {
        return _.uniq(nonErrorResults.map(r => r.supplier?.organization).filter(Boolean)).sort();
    }, [nonErrorResults]);

    // Determine service type: Direct if transshipment is empty, Transshipment if transshipment exists
    const serviceTypes = useMemo(() => {
        const types = new Set();
        nonErrorResults.forEach(r => {
            const transshipment = r.offer?.transshipment || '';
            if (transshipment && transshipment.trim() !== '') {
                types.add('Transshipment');
            } else {
                types.add('Direct');
            }
        });
        return Array.from(types).sort();
    }, [nonErrorResults]);

    // Determine rate source: Online Portals (platform !== -1) or Contract (platform === -1)
    const rateTypes = useMemo(() => {
        const types = new Set();
        nonErrorResults.forEach(r => {
            if (r.platform === -1) {
                types.add('Contract');
            } else {
                types.add('Online Portals');
            }
        });
        return Array.from(types).sort();
    }, [nonErrorResults]);

    // Get current filter values (handle backward compatibility with 'Products' type)
    const productTypeFilter = filters.find(f => f.type === 'Product Type' || f.type === 'Products');
    const carrierFilter = filters.find(f => f.type === 'Carrier');
    const serviceTypeFilter = filters.find(f => f.type === 'Service Type');
    const rateTypeFilter = filters.find(f => f.type === 'Rate Source' || f.type === 'Rate Type');

    const handleProductTypeChange = (value) => {
        // Remove both old 'Products' and new 'Product Type' filters for backward compatibility
        const newFilters = filters.filter(f => f.type !== 'Product Type' && f.type !== 'Products');
        if (value !== 'All') {
            newFilters.push({
                type: 'Product Type',
                value,
                description: value,
                func: (r) => _.get(r, 'product.type') === value
            });
        }
        setFilters(newFilters);
    };

    const handleCarrierChange = (value) => {
        const newFilters = filters.filter(f => f.type !== 'Carrier');
        if (value !== 'All') {
            newFilters.push({
                type: 'Carrier',
                value,
                description: value,
                func: (r) => _.get(r, 'supplier.organization') === value
            });
        }
        setFilters(newFilters);
    };

    const handleServiceTypeChange = (value) => {
        const newFilters = filters.filter(f => f.type !== 'Service Type');
        if (value !== 'All') {
            const isDirect = value === 'Direct';
            newFilters.push({
                type: 'Service Type',
                value,
                description: value,
                func: (r) => {
                    const transshipment = r.offer?.transshipment || '';
                    if (isDirect) {
                        // Direct: transshipment is empty or doesn't exist
                        return !transshipment || transshipment.trim() === '';
                    } else {
                        // Transshipment: transshipment exists and is not empty
                        return transshipment && transshipment.trim() !== '';
                    }
                }
            });
        }
        setFilters(newFilters);
    };

    const handleRateTypeChange = (value) => {
        const newFilters = filters.filter(f => f.type !== 'Rate Source');
        if (value !== 'All') {
            const isContract = value === 'Contract';
            newFilters.push({
                type: 'Rate Source',
                value,
                description: value,
                func: (r) => {
                    if (isContract) {
                        return r.platform === -1;
                    } else {
                        return r.platform !== -1;
                    }
                }
            });
        }
        setFilters(newFilters);
    };

    // Check if any filters are active
    const hasActiveFilters = productTypeFilter || carrierFilter || serviceTypeFilter || rateTypeFilter;

    const handleButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Button
                variant="text"
                {...(hasActiveFilters ? { color: 'primary' } : {})}
                onClick={handleButtonClick}
                sx={{
                    textTransform: 'none',
                    fontSize: '11px !important',
                    px: 1.5,
                    minWidth: '0px',
                    whiteSpace: 'nowrap',
                    borderRadius: 1,
                    height: 30,
                    background: 'none'
                }}
            >
                Filter
                <FilterIcon size={14} style={{ marginLeft: '6px', opacity: 0.6 }} />
            </Button>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{ 
                    root: { sx: { zIndex: 1000000 }},
                    paper: {
                        sx: {
                            minWidth: '400px',
                            p: 2,
                            mt: 1,
                        }
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                    }}
                >
                    {/* Product Type */}
                    {productTypes.length > 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                Product Type
                            </Typography>
                            <FormControl component="fieldset" size="small" fullWidth>
                                <RadioGroup
                                    value={(productTypeFilter && (productTypeFilter.type === 'Product Type' || productTypeFilter.type === 'Products')) ? productTypeFilter.value : 'All'}
                                    onChange={(e) => handleProductTypeChange(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="All"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="caption">All</Typography>}
                                    />
                                    {productTypes.map(type => (
                                        <FormControlLabel
                                            key={type}
                                            value={type}
                                            control={<Radio size="small" />}
                                            label={<Typography variant="caption">{type}</Typography>}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}

                    {/* Carrier - Always show */}
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            Carrier
                        </Typography>
                        <FormControl component="fieldset" size="small" fullWidth>
                            <RadioGroup
                                value={carrierFilter?.value || 'All'}
                                onChange={(e) => handleCarrierChange(e.target.value)}
                            >
                                <FormControlLabel
                                    value="All"
                                    control={<Radio size="small" />}
                                    label={<Typography variant="caption">All</Typography>}
                                />
                                {carriers.map(carrier => (
                                    <FormControlLabel
                                        key={carrier}
                                        value={carrier}
                                        control={<Radio size="small" />}
                                        label={<Typography variant="caption">{carrier}</Typography>}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Service Type */}
                    {serviceTypes.length > 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                Service Type
                            </Typography>
                            <FormControl component="fieldset" size="small" fullWidth>
                                <RadioGroup
                                    value={serviceTypeFilter?.value || 'All'}
                                    onChange={(e) => handleServiceTypeChange(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="All"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="caption">All</Typography>}
                                    />
                                    {serviceTypes.map(type => (
                                        <FormControlLabel
                                            key={type}
                                            value={type}
                                            control={<Radio size="small" />}
                                            label={<Typography variant="caption">{type}</Typography>}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}

                    {/* Rate Source */}
                    {rateTypes.length > 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                Rate Source
                            </Typography>
                            <FormControl component="fieldset" size="small" fullWidth>
                                <RadioGroup
                                    value={rateTypeFilter?.value || 'All'}
                                    onChange={(e) => handleRateTypeChange(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="All"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="caption">All</Typography>}
                                    />
                                    {rateTypes.map(type => (
                                        <FormControlLabel
                                            key={type}
                                            value={type}
                                            control={<Radio size="small" />}
                                            label={<Typography variant="caption">{type}</Typography>}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}
                </Box>
            </Popover>
        </>
    );
}

export default FiltersPanel;
